export type AnalyticsEventName =
  | "portal_register_view"
  | "auth_start"
  | "auth_complete"
  | "sign_up"
  | "onboarding_step_view"
  | "onboarding_step_complete"
  | "calendar_connected"
  | "onboarding_complete"
  | "marketing_plan_interest_captured"
  | "plan_selected"
  | "checkout_started"
  | "subscription_started"
  | "trial_started"
  | "trial_plan_selected"
  | "trial_payment_method_added"
  | "trial_converted"
  | "trial_paused"
  | "trial_reactivated";

type AnalyticsPrimitive = string | number | boolean | null | undefined;
type AnalyticsParams = Record<string, AnalyticsPrimitive>;

export type CalendarProvider = "google" | "microsoft";

export interface PlanAnalyticsInput {
  plan_id?: string | null;
  plan_key?: string | null;
  plan_name?: string | null;
  billing_period?: string | null;
  price?: number | null;
  currency?: string | null;
}

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

const allowedEvents = new Set<AnalyticsEventName>([
  "portal_register_view",
  "auth_start",
  "auth_complete",
  "sign_up",
  "onboarding_step_view",
  "onboarding_step_complete",
  "calendar_connected",
  "onboarding_complete",
  "marketing_plan_interest_captured",
  "plan_selected",
  "checkout_started",
  "subscription_started",
  "trial_started",
  "trial_plan_selected",
  "trial_payment_method_added",
  "trial_converted",
  "trial_paused",
  "trial_reactivated",
]);

const allowedParameters = new Set([
  "step_number",
  "step_name",
  "step_required",
  "plan_id",
  "plan_key",
  "plan_name",
  "billing_period",
  "price",
  "currency",
  "auth_method",
  "signup_method",
  "source_page",
  "calendar_provider",
  "cta_location",
  "page_path",
  "trial_plan_id",
  "trial_plan_key",
  "trial_days",
  "days_remaining",
  "selected_plan_id",
  "selected_plan_key",
  "marketing_plan_interest",
  "payment_method_status",
]);

const piiParameterNames = new Set([
  "email",
  "phone",
  "name",
  "business_name",
  "staff_name",
  "service_name",
  "calendar_email",
  "calendar_account",
  "address",
  "notes",
  "website",
  "instagram",
]);

const onceKeys = new Set<string>();
let debugEnabled = false;

function hasWindow(): boolean {
  return typeof window !== "undefined";
}

function getDataLayer(): Array<Record<string, unknown>> | null {
  if (!hasWindow()) {
    return null;
  }

  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}

function sanitizeParams(params: AnalyticsParams = {}): Record<string, AnalyticsPrimitive> {
  return Object.entries(params).reduce<Record<string, AnalyticsPrimitive>>((safe, [key, value]) => {
    if (!allowedParameters.has(key) || piiParameterNames.has(key) || typeof value === "undefined") {
      return safe;
    }

    if (key === "calendar_provider" && value !== "google" && value !== "microsoft") {
      return safe;
    }

    safe[key] = value;
    return safe;
  }, {});
}

export function setAnalyticsDebug(enabled: boolean) {
  debugEnabled = enabled;
}

export function resetAnalyticsForTests() {
  onceKeys.clear();
  debugEnabled = false;
  if (hasWindow()) {
    window.dataLayer = [];
  }
}

export function pushDataLayerEvent(event: AnalyticsEventName, params: AnalyticsParams = {}): boolean {
  if (!allowedEvents.has(event)) {
    return false;
  }

  const dataLayer = getDataLayer();
  if (!dataLayer) {
    return false;
  }

  const payload = {
    event,
    ...sanitizeParams(params),
  };

  dataLayer.push(payload);

  if (debugEnabled) {
    console.info("[analytics]", payload);
  }

  return true;
}

export function pushDataLayerEventOnce(
  onceKey: string,
  event: AnalyticsEventName,
  params: AnalyticsParams = {}
): boolean {
  if (onceKeys.has(onceKey)) {
    return false;
  }

  const pushed = pushDataLayerEvent(event, params);
  if (pushed) {
    onceKeys.add(onceKey);
  }

  return pushed;
}

export function installGtmContainer(gtmId: string | undefined | null) {
  if (!hasWindow() || !gtmId) {
    return;
  }

  const existing = document.querySelector(`script[data-gtm-id="${gtmId}"]`);
  if (existing) {
    return;
  }

  const dataLayer = getDataLayer();
  dataLayer?.push({
    "gtm.start": Date.now(),
    event: "gtm.js",
  });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`;
  script.dataset.gtmId = gtmId;
  document.head.appendChild(script);
}

export function trackPortalRegisterView(pagePath: string) {
  return pushDataLayerEvent("portal_register_view", { page_path: pagePath });
}

export function trackAuthStart(authMethod: string) {
  return pushDataLayerEvent("auth_start", { auth_method: authMethod });
}

export function trackAuthComplete(authMethod = "unknown", sourcePage?: string) {
  return pushDataLayerEventOnce(`auth_complete:${sourcePage || ""}`, "auth_complete", {
    auth_method: authMethod,
    source_page: sourcePage,
    page_path: sourcePage,
  });
}

export function trackSignUp() {
  return pushDataLayerEventOnce("sign_up:self_service", "sign_up", {
    signup_method: "self_service",
    source_page: "/register",
  });
}

export function trackOnboardingStepView(stepNumber: number, stepName: string, stepRequired = true) {
  return pushDataLayerEvent("onboarding_step_view", {
    step_number: stepNumber,
    step_name: stepName,
    step_required: stepRequired,
  });
}

export function trackOnboardingStepComplete(stepNumber: number, stepName: string, stepRequired = true) {
  return pushDataLayerEventOnce(`onboarding_step_complete:${stepName}`, "onboarding_step_complete", {
    step_number: stepNumber,
    step_name: stepName,
    step_required: stepRequired,
  });
}

export function trackCalendarConnected(calendarProvider: CalendarProvider) {
  return pushDataLayerEventOnce(`calendar_connected:${calendarProvider}`, "calendar_connected", {
    calendar_provider: calendarProvider,
    step_name: "calendar_connection",
  });
}

export function trackOnboardingComplete() {
  return pushDataLayerEventOnce("onboarding_complete", "onboarding_complete");
}

export function trackMarketingPlanInterestCaptured(planKey: string | null | undefined, pagePath?: string) {
  return pushDataLayerEventOnce(`marketing_plan_interest:${planKey || ""}`, "marketing_plan_interest_captured", {
    marketing_plan_interest: planKey || undefined,
    page_path: pagePath,
  });
}

export function trackPlanSelected(plan: PlanAnalyticsInput) {
  const fallbackPlanId = plan.plan_id || plan.plan_key || undefined;
  return pushDataLayerEvent("plan_selected", {
    plan_id: fallbackPlanId,
    plan_key: plan.plan_key || undefined,
    plan_name: plan.plan_name || undefined,
    billing_period: plan.billing_period || "monthly",
    price: plan.price,
    currency: plan.currency,
  });
}

export function trackTrialStarted(params: {
  trial_plan_id?: string | null;
  trial_plan_key?: string | null;
  trial_days?: number | null;
  days_remaining?: number | null;
}) {
  return pushDataLayerEventOnce(
    `trial_started:${params.trial_plan_id || params.trial_plan_key || ""}`,
    "trial_started",
    {
      trial_plan_id: params.trial_plan_id || undefined,
      trial_plan_key: params.trial_plan_key || undefined,
      trial_days: params.trial_days,
      days_remaining: params.days_remaining,
    }
  );
}

export function trackTrialPlanSelected(params: {
  trial_plan_id?: string | null;
  trial_plan_key?: string | null;
  selected_plan_id?: string | null;
  selected_plan_key?: string | null;
  payment_method_status?: string | null;
}) {
  return pushDataLayerEvent("trial_plan_selected", {
    trial_plan_id: params.trial_plan_id || undefined,
    trial_plan_key: params.trial_plan_key || undefined,
    selected_plan_id: params.selected_plan_id || undefined,
    selected_plan_key: params.selected_plan_key || undefined,
    payment_method_status: params.payment_method_status || undefined,
  });
}

export function trackTrialPaymentMethodAdded(paymentMethodStatus?: string | null) {
  return pushDataLayerEventOnce("trial_payment_method_added", "trial_payment_method_added", {
    payment_method_status: paymentMethodStatus || undefined,
  });
}
